import React, { useState } from 'react';
import { formatPercentage, formatCurrency } from './shared/formatting';
import EditableInput from './shared/EditableInput';

/**
 * Trading Parameters Section for Create/Edit Group Modal
 */
const TradingParametersSection = ({
  formData,
  formErrors,
  touched,
  loading,
  onInputChange,
  onBlur
}) => {
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({});

  const handleValueClick = (field) => {
    setEditingField(field);
    setTempValues(prev => ({
      ...prev,
      [field]: formData[field].toString()
    }));
  };

  const handleTempChange = (field, value) => {
    setTempValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      handleSubmit(field);
    } else if (e.key === 'Escape') {
      handleCancel(field);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const currentValue = parseFloat(tempValues[field]) || 0;
      const step = getStepForField(field);
      const newValue = e.key === 'ArrowUp' ? currentValue + step : Math.max(getMinForField(field), currentValue - step);
      handleTempChange(field, newValue.toString());
    }
  };

  const handleSubmit = (field) => {
    const value = parseFloat(tempValues[field]) || 0;
    onInputChange(field, value);
    setEditingField(null);
    setTempValues(prev => {
      const newTemp = { ...prev };
      delete newTemp[field];
      return newTemp;
    });
  };

  const handleCancel = (field) => {
    setEditingField(null);
    setTempValues(prev => {
      const newTemp = { ...prev };
      delete newTemp[field];
      return newTemp;
    });
  };

  const handleInputBlur = (field) => {
    setTimeout(() => {
      if (editingField === field) {
        handleSubmit(field);
      }
    }, 200);
  };

  const getStepForField = (field) => {
    switch (field) {
      case 'commission': return 0.0001;
      case 'swap': return 0.0001;
      case 'defaultSpread': return 0.00001;
      case 'maxPositionSize': return 1000;
      default: return 1;
    }
  };

  const getMinForField = (field) => {
    switch (field) {
      case 'commission': return 0;
      case 'swap': return 0;
      case 'defaultSpread': return 0;
      case 'maxPositionSize': return 1000;
      default: return 0;
    }
  };

  const getMaxForField = (field) => {
    switch (field) {
      case 'commission': return 0.01;
      case 'swap': return 0.01;
      case 'defaultSpread': return 0.001;
      case 'maxPositionSize': return 1000000;
      default: return 100;
    }
  };

  const formatValue = (field, value) => {
    switch (field) {
      case 'commission':
      case 'swap':
      case 'defaultSpread':
        return formatPercentage(value);
      case 'maxPositionSize':
        return formatCurrency(value);
      default:
        return value.toString();
    }
  };

  const renderInputField = (field) => {
    return (
      <EditableInput
        value={formatValue(field, formData[field])}
        isEditing={editingField === field}
        tempValue={tempValues[field] || ''}
        onEdit={() => handleValueClick(field)}
        onChange={(value) => handleTempChange(field, value)}
        onSubmit={() => handleSubmit(field)}
        onCancel={() => handleCancel(field)}
        onKeyDown={(e) => handleKeyDown(e, field)}
        onBlur={() => handleInputBlur(field)}
        min={getMinForField(field)}
        max={getMaxForField(field)}
        step={getStepForField(field)}
      />
    );
  };

  const rangeClass = (field) =>
    `gm-trade-range ${formErrors[field] && touched[field] ? 'gm-form-error-input' : ''}`;

  return (
    <div className="gm-trade-grid">
      <div className="gm-trade-card">
        <div className="gm-trade-header">
          <span className="gm-trade-label">Commission Rate</span>
          {renderInputField('commission')}
        </div>
        <input
          type="range"
          min="0"
          max="0.01"
          step="0.0001"
          value={formData.commission}
          onChange={(e) => onInputChange('commission', parseFloat(e.target.value))}
          onBlur={() => onBlur('commission')}
          disabled={loading}
          className={rangeClass('commission')}
        />
        {formErrors.commission && touched.commission && (
          <span className="gm-form-error-text">{formErrors.commission}</span>
        )}
      </div>

      <div className="gm-trade-card">
        <div className="gm-trade-header">
          <span className="gm-trade-label">Swap Rate</span>
          {renderInputField('swap')}
        </div>
        <input
          type="range"
          min="0"
          max="0.01"
          step="0.0001"
          value={formData.swap}
          onChange={(e) => onInputChange('swap', parseFloat(e.target.value))}
          onBlur={() => onBlur('swap')}
          disabled={loading}
          className={rangeClass('swap')}
        />
        {formErrors.swap && touched.swap && (
          <span className="gm-form-error-text">{formErrors.swap}</span>
        )}
      </div>

      <div className="gm-trade-card">
        <div className="gm-trade-header">
          <span className="gm-trade-label">Swap Enabled</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.swapEnabled || false}
              onChange={(e) => onInputChange('swapEnabled', e.target.checked)}
              disabled={loading}
              style={{ width: 18, height: 18, accentColor: 'var(--admin-primary)' }}
            />
            <span style={{ fontSize: '0.875rem', color: 'var(--admin-text-secondary)' }}>
              {formData.swapEnabled ? 'On' : 'Off'}
            </span>
          </label>
        </div>
      </div>

      {formData.swapEnabled && (
        <>
          <div className="gm-trade-card">
            <div className="gm-trade-header">
              <span className="gm-trade-label">Swap Time (UTC)</span>
              <EditableInput
                value={formData.swapTime || '22:00'}
                isEditing={editingField === 'swapTime'}
                tempValue={tempValues.swapTime || ''}
                onEdit={() => handleValueClick('swapTime')}
                onChange={(value) => handleTempChange('swapTime', value)}
                onSubmit={() => handleSubmit('swapTime')}
                onCancel={() => handleCancel('swapTime')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit('swapTime');
                  if (e.key === 'Escape') handleCancel('swapTime');
                }}
                onBlur={() => handleInputBlur('swapTime')}
              />
            </div>
            <input
              type="text"
              placeholder="HH:mm"
              value={formData.swapTime || '22:00'}
              onChange={(e) => onInputChange('swapTime', e.target.value)}
              onBlur={() => onBlur('swapTime')}
              disabled={loading}
              className={rangeClass('swapTime')}
              style={{ fontSize: '0.875rem', padding: '8px 12px', width: '100%', border: '1px solid #d1d5db', borderRadius: 6 }}
            />
            {formErrors.swapTime && touched.swapTime && (
              <span className="gm-form-error-text">{formErrors.swapTime}</span>
            )}
          </div>

          <div className="gm-trade-card">
            <div className="gm-trade-header">
              <span className="gm-trade-label">Triple Swap Day</span>
            </div>
            <select
              value={formData.tripleSwapDay || 'Wednesday'}
              onChange={(e) => onInputChange('tripleSwapDay', e.target.value)}
              onBlur={() => onBlur('tripleSwapDay')}
              disabled={loading}
              style={{ fontSize: '0.875rem', padding: '8px 12px', width: '100%', border: '1px solid #d1d5db', borderRadius: 6 }}
            >
              {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </>
      )}

      <div className="gm-trade-card">
        <div className="gm-trade-header">
          <span className="gm-trade-label">Default Spread</span>
          {renderInputField('defaultSpread')}
        </div>
        <input
          type="range"
          min="0"
          max="0.001"
          step="0.00001"
          value={formData.defaultSpread}
          onChange={(e) => onInputChange('defaultSpread', parseFloat(e.target.value))}
          onBlur={() => onBlur('defaultSpread')}
          disabled={loading}
          className={rangeClass('defaultSpread')}
        />
        {formErrors.defaultSpread && touched.defaultSpread && (
          <span className="gm-form-error-text">{formErrors.defaultSpread}</span>
        )}
      </div>

      <div className="gm-trade-card">
        <div className="gm-trade-header">
          <span className="gm-trade-label">Max Position Size</span>
          {renderInputField('maxPositionSize')}
        </div>
        <input
          type="range"
          min="1000"
          max="1000000"
          step="1000"
          value={formData.maxPositionSize}
          onChange={(e) => onInputChange('maxPositionSize', parseFloat(e.target.value))}
          onBlur={() => onBlur('maxPositionSize')}
          disabled={loading}
          className={rangeClass('maxPositionSize')}
        />
        {formErrors.maxPositionSize && touched.maxPositionSize && (
          <span className="gm-form-error-text">{formErrors.maxPositionSize}</span>
        )}
      </div>
    </div>
  );
};

export default TradingParametersSection;
